import React, { useEffect, useRef, useState } from "react";
import { Modal, Box, IconButton, Typography, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraIcon from "@mui/icons-material/Camera";
import { useTranslation } from "react-i18next";
import { CAMERA_VIDEO_CONSTRAINTS, captureVideoFrame } from "../utils/submissionImage";

export default function CameraCapture({ open, onClose, onCapture, onFallbackToFile }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [captureCount, setCaptureCount] = useState(0);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setError("");
    setCaptureCount(0);

    navigator.mediaDevices
      ?.getUserMedia({ video: CAMERA_VIDEO_CONSTRAINTS })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        if (!cancelled) setError(t("تعذّر الوصول إلى الكاميرا."));
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [open, t]);

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const blob = await captureVideoFrame(video);
    if (blob) {
      onCapture(blob);
      setCaptureCount((count) => count + 1);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#000",
          outline: "none",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            insetInlineEnd: 12,
            color: "#fff",
            bgcolor: "rgba(255, 255, 255, 0.12)",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.24)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {error ? (
          <Box sx={{ textAlign: "center", color: "#fff", px: 3 }}>
            <Typography sx={{ mb: 2 }}>{error}</Typography>
            <Button variant="outlined" sx={{ color: "#fff", borderColor: "#fff" }} onClick={onFallbackToFile}>
              {t("اختر صورة من الملفات بدلاً من ذلك")}
            </Button>
          </Box>
        ) : (
          <>
            {captureCount > 0 && (
              <Typography sx={{ color: "#fff", mb: 1, fontSize: 14 }}>
                {t("عدد الصور: {{count}} — أضف صوراً أخرى أو أغلق عند الانتهاء", { count: captureCount })}
              </Typography>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ maxWidth: "100%", maxHeight: "78vh" }}
            />
            <IconButton
              onClick={handleCapture}
              sx={{
                mt: 3,
                width: 68,
                height: 68,
                bgcolor: "#fff",
                border: "4px solid rgba(255, 255, 255, 0.4)",
                "&:hover": { bgcolor: "#eee" },
              }}
            >
              <CameraIcon sx={{ fontSize: 32, color: "#111" }} />
            </IconButton>
            {captureCount > 0 && (
              <Button
                onClick={onClose}
                sx={{ mt: 2, color: "#fff" }}
              >
                {t("تم، الانتقال للمراجعة")}
              </Button>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
}
